"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DealDeviceTermSchema = exports.DeviceIntroSchema = exports.DEVICE_COVER_MODES = exports.DEVICE_DISCOUNT_TARGETS = void 0;
exports.deviceListPricing = deviceListPricing;
exports.deviceDealTiers = deviceDealTiers;
exports.deviceTierTotals = deviceTierTotals;
exports.deviceTermIsPriced = deviceTermIsPriced;
exports.showsOneTime = showsOneTime;
exports.deviceQuoteTerms = deviceQuoteTerms;
exports.deviceQuoteTiers = deviceQuoteTiers;
exports.dealCardTotals = dealCardTotals;
exports.termAppliesAtLocation = termAppliesAtLocation;
exports.introAmountCents = introAmountCents;
const zod_1 = require("zod");
const planAssignment_1 = require("./planAssignment");
/**
 * DEVICE DEAL TERMS — what a Deal changes about ONE device finance option.
 *
 * A Deal already discounts SOFTWARE packages (`Deal.packageDiscounts`, priced
 * by `dealPackagesTotals` and reconciled server-side by `dealPricedPerPackage`).
 * Hardware had no such thing: `Deal.devicePackageIds` is a bare list of SKU ids,
 * so a device named on a deal has always been quoted and billed at the catalog's
 * list price. This is the shape that closes that.
 *
 * ---------------------------------------------------------------------------
 * KEYED BY SKU, WHICH IS THE FINANCE OPTION — NOT BY DEVICE
 * ---------------------------------------------------------------------------
 *
 * One physical terminal offered outright, on 24-month instalments and on
 * monthly rental is THREE `device_finance` SKUs sharing a `bundleId`/`modelId`
 * (see `groupDevices`). They are three different commercial offers and a deal
 * discounts them independently — $50 off the outright price says nothing about
 * what the rental should cost. So terms are keyed by `skuId`, the finance
 * option's own identity, exactly as `packageDiscounts` is keyed by the package
 * it is attributable to.
 *
 * ---------------------------------------------------------------------------
 * TWO PLACES THE MONEY CAN COME OFF
 * ---------------------------------------------------------------------------
 *
 * A financed device has two amounts a customer cares about: what they pay every
 * month, and what they pay TODAY (the down payment). `takenFrom` says which one
 * the discount reduces, because the same $20 reads as a completely different
 * offer depending on where it lands — "$20/mo cheaper for 24 months" is $480,
 * "$20 off your down payment" is $20. Guessing between them would misprice the
 * deal by a factor of the term.
 *
 * ---------------------------------------------------------------------------
 * HOW MANY UNITS IT COVERS
 * ---------------------------------------------------------------------------
 *
 * `covers: 'each'` — every unit the tenant adds gets the deal price, however
 * many they add. `covers: 'first_n'` — the first `coveredUnits` get it and the
 * rest bill at catalog list, as two separate lines (see `deviceDealTiers`).
 * The quantity is never CAPPED: a tenant who wants a third terminal buys a
 * third terminal, at the standard price, rather than being blocked by an offer.
 *
 * PER LOCATION, deliberately. Devices are chosen per location in the onboarding
 * wizard and `buildLocationBilling` prices one location at a time with no
 * cross-location state; a tenant-wide count would have to be applied in draft
 * order, making the price of location 3's terminal depend on what location 1
 * happened to pick. `coveredUnits` therefore means "per location", which is
 * also the only reading the wizard's own per-location quantity stepper can show
 * honestly.
 *
 * Every field is OPTIONAL — an added field is never required (shared-types
 * rule), and a deal that predates this carries no `deviceTerms` at all, which
 * reads as "every device on this deal bills at list", i.e. exactly what such a
 * deal has always done.
 */
/** Which of a device's two amounts a deal discount comes off. */
exports.DEVICE_DISCOUNT_TARGETS = ['monthly', 'one_time'];
/** How many units of a device the deal's terms cover. */
exports.DEVICE_COVER_MODES = ['each', 'first_n'];
/**
 * A LIMITED-TIME DEVICE PRICE — the device counterpart of a package's intro.
 *
 * Software could already be offered at an introductory rate for N months
 * (`packageIntros`); hardware could only be discounted FOREVER. So "the
 * terminal rents at half price for three months, then full" had nowhere to
 * live, and a device promotion never reached billing at all.
 *
 * Expressed the same two ways a discount is — a fixed price OR a percentage —
 * so an operator writes a device promotion exactly as they write a package one.
 * `months` counts BILLING CYCLES, the same unit as `introMonths`, `termMonths`
 * and Stripe's own phase `iterations`. Nothing in this system is expressed in
 * days; a day-based promotion cannot be mapped onto a monthly billing cycle
 * without lying about when the price changes.
 */
exports.DeviceIntroSchema = zod_1.z.object({
    /** What the customer pays per cycle during the intro. `0` is real: free. */
    priceCents: zod_1.z.number().int().min(0).optional(),
    /** …or a percentage off the standard amount, for the same period. */
    percentOff: zod_1.z.number().min(0).max(100).optional(),
    /** Billing cycles the intro runs for. Never 0 — that is not an offer. */
    months: zod_1.z.number().int().min(1),
    /** Internal note. Never leaves the server — see `Discount.reason`. */
    reason: zod_1.z.string().optional(),
}).refine((i) => (i.priceCents !== undefined ? 1 : 0) + (i.percentOff !== undefined ? 1 : 0) === 1, { message: 'exactly one of priceCents or percentOff is required', path: ['priceCents'] });
exports.DealDeviceTermSchema = zod_1.z.object({
    /**
     * The reduction, in the EXACT shape `packageDiscounts` uses. Reused rather
     * than re-invented so a device discount validates, rounds and audits by the
     * same rules a package discount does — `applyDiscount` is the only arithmetic
     * either of them goes through.
     */
    discount: planAssignment_1.DiscountSchema.optional(),
    /** Default `'monthly'` when absent — the amount a customer compares offers on. */
    takenFrom: zod_1.z.enum(exports.DEVICE_DISCOUNT_TARGETS).optional(),
    /** Default `'each'` when absent — the offer applies however many they take. */
    covers: zod_1.z.enum(exports.DEVICE_COVER_MODES).optional(),
    /** Units covered when `covers === 'first_n'`. Per location — see the header. */
    coveredUnits: zod_1.z.number().int().min(1).optional(),
    /**
     * HOW MANY LOCATIONS THIS OFFER REACHES.
     *
     * Absent (the norm) means every location. `2` means the deal's device terms
     * apply at the tenant's first two locations and every location after that
     * buys the same device at catalogue list.
     *
     * ORDERED THE SAME WAY THE FREE-LOCATION QUOTA IS — `index < coveredLocations`
     * over the draft's locations. That is deliberate: an order-dependent rule
     * already exists and is understood (`freeLocationQuota` decides which
     * location pays no plan fee the same way), so this adds no NEW notion of
     * "which location came first". Reusing it also means the two rules can never
     * disagree about the ordering, which two independent orderings eventually
     * would.
     *
     * It does not cap anything: a tenant opening a third store still buys
     * terminals, at list, rather than being blocked by an offer.
     */
    coveredLocations: zod_1.z.number().int().min(1).optional(),
    /**
     * A limited-time price for this device, on top of (not instead of) any
     * permanent `discount`. See `DeviceIntroSchema`.
     */
    intro: exports.DeviceIntroSchema.optional(),
    /**
     * The deal's own down payment, overriding the SKU's `downPaymentCents`.
     * `0` is MEANINGFUL and is not the same as absent: it is the "$0 down" a deal
     * exists to advertise, and it must beat a SKU that asks for $99.
     */
    downPaymentCents: zod_1.z.number().int().min(0).optional(),
    /**
     * Whether the one-time amount appears on the CUSTOMER-FACING preview.
     * Default `true` when absent.
     *
     * ⚠️ PRESENTATION ONLY. A hidden amount is still billed, still on the
     * invoice, and still in every total this file computes — `deviceDealTiers`
     * does not read this field at all. It exists so an operator can market a
     * clean "$149/mo" without the setup fee competing with it, NOT so a customer
     * can be charged something they were never shown a total for. Any surface
     * that hides it must still show a total that includes it.
     */
    showOneTimeInPreview: zod_1.z.boolean().optional(),
}).refine(
// A 'first_n' with no n is not a smaller offer, it is an UNDEFINED one — and
// the honest fallbacks disagree: "covers 1" and "covers everything" are
// different prices. Refused at the schema so no consumer has to pick.
(t) => t.covers !== 'first_n' || typeof t.coveredUnits === 'number', { message: 'coveredUnits is required when covers is "first_n"', path: ['coveredUnits'] });
/** What one device SKU costs at list, before any deal touches it. */
function deviceListPricing(sku) {
    const amount = Math.max(0, Math.round(sku.amountCents || 0));
    switch (sku.saleMode) {
        // Given away with the plan: it still ships, it just never bills (see
        // `buildLocationBilling`, which puts it on the order and emits no line).
        case 'included':
            return { monthlyCents: 0, oneTimeCents: 0 };
        // Bought once, up front. There is no monthly side to discount.
        case 'outright':
            return { monthlyCents: 0, oneTimeCents: amount };
        // Financed or rented: `amountCents` is the RECURRING figure and the
        // down payment is the separate one-time. A security deposit is NOT here on
        // purpose — it is refundable, so it is not a price, and the server bills it
        // on its own synthetic `<skuId>_deposit` line.
        default:
            return { monthlyCents: amount, oneTimeCents: Math.max(0, Math.round(sku.downPaymentCents || 0)) };
    }
}
/**
 * THE ONE PLACE A DEAL'S DEVICE TERMS BECOME PRICES.
 *
 * Pure and total. Both the browser (deal preview, wizard, review-and-pay) and
 * the server (`buildLocationBilling`) call this and nothing else, which is what
 * makes "the number quoted is the number charged" structural rather than a
 * coincidence two implementations arrive at separately.
 *
 * Returns ONE tier when the whole quantity prices the same way, and TWO when a
 * `first_n` term is exceeded — covered units first, then the remainder at list.
 * A quantity of 0 returns `[]`: no units, no lines, no zero-quantity row for a
 * caller to render.
 *
 * ROUNDING happens exactly once, inside `applyDiscount`, on the PER-UNIT
 * amount — never on a multiplied total that is then divided back out. That is
 * what keeps `quantity × perUnit` the same number wherever it is computed, and
 * it is the same rule `composeSubscription` follows for software.
 *
 * A discount can never push an amount below zero (`applyDiscount` floors at 0)
 * and never above list, so a "discount" can never become a surcharge.
 */
function deviceDealTiers(args) {
    const quantity = Math.max(0, Math.floor(args.quantity || 0));
    if (quantity === 0)
        return [];
    const list = deviceListPricing(args.sku);
    // Beyond the offer's reach, the deal simply does not apply here — the same
    // answer as having no terms at all, which the branch below already handles.
    const term = (typeof args.locationIndex === 'number'
        && !termAppliesAtLocation(args.term, args.locationIndex))
        ? undefined
        : args.term;
    // No terms at all: one tier, at list, flagged as not from the deal so a
    // caller never draws a strikethrough against an identical number.
    if (!term) {
        return [{
                quantity,
                monthlyCents: list.monthlyCents,
                oneTimeCents: list.oneTimeCents,
                listMonthlyCents: list.monthlyCents,
                listOneTimeCents: list.oneTimeCents,
                fromDeal: false,
            }];
    }
    // The deal's own down payment REPLACES the SKU's before anything is
    // discounted, and `0` replaces it just as firmly as `99` does — "$0 down" is
    // the offer, not a missing value. `??` (not `||`) is what makes that true.
    const baseOneTime = typeof term.downPaymentCents === 'number'
        ? Math.max(0, Math.round(term.downPaymentCents))
        : list.oneTimeCents;
    /**
     * WHERE THE DISCOUNT ACTUALLY LANDS.
     *
     * `'monthly'` is the default because it is the amount a customer compares
     * offers on — but a device with NO monthly side cannot be discounted there.
     * An outright purchase is the whole of that case: its `amountCents` is a
     * one-time price and `deviceListPricing` puts it entirely on `oneTimeCents`,
     * so a `'monthly'` discount would be applied to 0 and SILENTLY VANISH. The
     * operator types "$50 off", the row shows no change, and the deal sells at
     * full price.
     *
     * So the target is corrected here rather than trusted from the term. Doing it
     * in the UI alone would leave every already-stored term wrong and every other
     * writer (a script, the API, a future screen) free to reintroduce it; doing it
     * here makes "a discount on an outright device comes off its price" true for
     * anything that calls this function.
     *
     * Only ever corrects TOWARDS the amount that exists — never the reverse. A
     * financed device keeps whichever target was chosen, including `'one_time'`
     * against a zero down payment, which is a real (if empty) offer rather than a
     * mistake.
     */
    const requested = term.takenFrom || 'monthly';
    const takenFrom = requested === 'monthly' && list.monthlyCents === 0
        ? 'one_time'
        : requested;
    const dealMonthly = takenFrom === 'monthly'
        ? (0, planAssignment_1.applyDiscount)(list.monthlyCents, term.discount)
        : list.monthlyCents;
    const dealOneTime = takenFrom === 'one_time'
        ? (0, planAssignment_1.applyDiscount)(baseOneTime, term.discount)
        : baseOneTime;
    const dealTier = (n) => ({
        quantity: n,
        monthlyCents: dealMonthly,
        oneTimeCents: dealOneTime,
        listMonthlyCents: list.monthlyCents,
        // The LIST one-time is the SKU's own, not the deal's override — otherwise a
        // deal that sets "$0 down" against a SKU asking $99 would show no saving at
        // all, which is precisely the saving it was built to advertise.
        listOneTimeCents: list.oneTimeCents,
        fromDeal: true,
    });
    const listTier = (n) => ({
        quantity: n,
        monthlyCents: list.monthlyCents,
        oneTimeCents: list.oneTimeCents,
        listMonthlyCents: list.monthlyCents,
        listOneTimeCents: list.oneTimeCents,
        fromDeal: false,
    });
    if ((term.covers || 'each') === 'each')
        return [dealTier(quantity)];
    // `covers: 'first_n'`. The schema guarantees `coveredUnits` is present, but
    // this function is called with rows read from storage as well as with freshly
    // validated ones, so an absent value falls back to covering NOTHING rather
    // than inventing a count — the conservative direction is the one that charges
    // list, never the one that gives hardware away.
    const covered = Math.max(0, Math.floor(term.coveredUnits ?? 0));
    if (covered <= 0)
        return [listTier(quantity)];
    if (covered >= quantity)
        return [dealTier(quantity)];
    return [dealTier(covered), listTier(quantity - covered)];
}
/** Sums tiers. Separate from `deviceDealTiers` so a caller can render the tiers AND the total from one array. */
function deviceTierTotals(tiers) {
    return (tiers || []).reduce((acc, tier) => ({
        monthlyCents: acc.monthlyCents + tier.monthlyCents * tier.quantity,
        oneTimeCents: acc.oneTimeCents + tier.oneTimeCents * tier.quantity,
        listMonthlyCents: acc.listMonthlyCents + tier.listMonthlyCents * tier.quantity,
        listOneTimeCents: acc.listOneTimeCents + tier.listOneTimeCents * tier.quantity,
    }), { monthlyCents: 0, oneTimeCents: 0, listMonthlyCents: 0, listOneTimeCents: 0 });
}
/**
 * Whether a term actually changes anything about what a SKU costs.
 *
 * An "empty" term — no discount, no down-payment override — is a row an
 * operator opened and closed. It must not draw a discount badge, must not mint
 * a Stripe price, and must not make a line look deal-priced when it is not.
 */
function deviceTermIsPriced(term) {
    if (!term)
        return false;
    return Boolean(term.discount) || typeof term.downPaymentCents === 'number';
}
/**
 * Whether the one-time amount may be shown on a CUSTOMER-facing surface.
 * Absent means yes — hiding money is the choice that has to be made explicitly.
 */
function showsOneTime(term) {
    return term?.showOneTimeInPreview !== false;
}
/**
 * Derive the wire shape from a deal's terms and a catalog SKU. Pure.
 *
 * Returns `undefined` for a term that changes no price — an operator opened the
 * drawer and closed it — so an unpriced term never reaches the browser and
 * never draws a discount badge against an identical number.
 */
function deviceQuoteTerms(sku, term) {
    if (!deviceTermIsPriced(term))
        return undefined;
    // ONE unit through the same arithmetic the server bills from, so the per-unit
    // figure on the wire is by construction the per-unit figure charged.
    const [tier] = deviceDealTiers({ sku, quantity: 1, term: { ...term, covers: 'each' } });
    if (!tier)
        return undefined;
    // Resolved ONCE, here, against the deal's own monthly figure: a percentage
    // promotion stacks on top of the permanent discount, and doing that in two
    // places is how two consumers come to disagree by a cent.
    const introMonthlyCents = introAmountCents(term?.intro, tier.monthlyCents);
    return {
        skuId: sku.skuId,
        monthlyCents: tier.monthlyCents,
        oneTimeCents: tier.oneTimeCents,
        listMonthlyCents: tier.listMonthlyCents,
        listOneTimeCents: tier.listOneTimeCents,
        covers: term?.covers || 'each',
        ...(typeof term?.coveredUnits === 'number' ? { coveredUnits: term.coveredUnits } : {}),
        ...(typeof term?.coveredLocations === 'number' ? { coveredLocations: term.coveredLocations } : {}),
        ...(typeof introMonthlyCents === 'number' && term?.intro
            ? { introMonthlyCents, introMonths: term.intro.months }
            : {}),
        showOneTime: showsOneTime(term),
    };
}
/**
 * The browser's tiering, from the wire shape — the mirror of `deviceDealTiers`
 * for a caller that has resolved PRICES rather than a discount.
 *
 * Kept as its own function, and deliberately not as "rebuild a DealDeviceTerm
 * and call deviceDealTiers", because the browser has no discount to re-apply:
 * re-deriving one from (list - deal) and applying it again would round a second
 * time and could land a cent away from what the server charges.
 */
function deviceQuoteTiers(terms, listPricing, rawQuantity, 
/**
 * WHICH LOCATION THIS IS, 0-based, in draft order.
 *
 * Optional so every existing caller is unchanged — omitting it prices as
 * though the offer reaches everywhere, which is what an offer with no
 * `coveredLocations` does anyway. A caller that DOES pass it gets the deal at
 * the first N locations and catalogue list beyond, per `termAppliesAtLocation`.
 */
locationIndex) {
    const quantity = Math.max(0, Math.floor(rawQuantity || 0));
    if (quantity === 0)
        return [];
    const listTier = (n) => ({
        quantity: n,
        monthlyCents: listPricing.monthlyCents,
        oneTimeCents: listPricing.oneTimeCents,
        listMonthlyCents: listPricing.monthlyCents,
        listOneTimeCents: listPricing.oneTimeCents,
        fromDeal: false,
    });
    if (!terms)
        return [listTier(quantity)];
    // Beyond the offer's reach: this location buys the same device at list. Not
    // blocked, not discounted — the third store still gets its terminal.
    if (typeof locationIndex === 'number'
        && !termAppliesAtLocation({ coveredLocations: terms.coveredLocations }, locationIndex)) {
        return [listTier(quantity)];
    }
    const dealTier = (n) => ({
        quantity: n,
        monthlyCents: terms.monthlyCents,
        oneTimeCents: terms.oneTimeCents,
        listMonthlyCents: terms.listMonthlyCents,
        listOneTimeCents: terms.listOneTimeCents,
        fromDeal: true,
    });
    if (terms.covers === 'each')
        return [dealTier(quantity)];
    // Same conservative fallback as `deviceDealTiers`: a covered count that is
    // missing or zero covers nothing, so the customer is quoted list rather than
    // being shown hardware the deal never gave away.
    const covered = Math.max(0, Math.floor(terms.coveredUnits ?? 0));
    if (covered <= 0)
        return [listTier(quantity)];
    if (covered >= quantity)
        return [dealTier(quantity)];
    return [dealTier(covered), listTier(quantity - covered)];
}
/**
 * WHAT THE CARD SAYS, for the options currently selected.
 *
 * A customer choosing "Rent" instead of "Finance" is choosing a different
 * monthly bill, so the headline has to move with them — a card whose big number
 * ignores the hardware buttons underneath it is quoting a price nobody pays.
 *
 * Shared by the live landing card (platform-app) and the deal builder's preview
 * of it (super-admin-ui) so the operator's mirror cannot drift from the real
 * thing. It only ADDS UP already-priced options; every discount was applied
 * upstream by `deviceDealTiers`, and nothing here rounds anything.
 *
 * An `included` device contributes nothing on either cadence — it ships free.
 */
function dealCardTotals(softwareMonthlyCents, selected) {
    const software = Math.max(0, Math.round(softwareMonthlyCents || 0));
    let deviceMonthly = 0;
    let listDeviceMonthly = 0;
    let dueToday = 0;
    let listDueToday = 0;
    for (const option of selected) {
        if (!option || option.saleMode === 'included')
            continue;
        if (option.saleMode === 'outright') {
            // Bought once: the whole price is due today and nothing recurs.
            dueToday += option.amountCents;
            listDueToday += option.listAmountCents;
            continue;
        }
        // Financed or rented: a monthly line AND, where there is one, a down
        // payment today. Both, not either — the two are separate invoices.
        deviceMonthly += option.amountCents;
        listDeviceMonthly += option.listAmountCents;
        dueToday += option.downPaymentCents;
        listDueToday += option.listDownPaymentCents;
    }
    return {
        monthlyCents: software + deviceMonthly,
        listMonthlyCents: software + listDeviceMonthly,
        dueTodayCents: dueToday,
        listDueTodayCents: listDueToday,
        monthlyDiscounted: listDeviceMonthly > deviceMonthly,
        dueTodayDiscounted: listDueToday > dueToday,
        softwareMonthlyCents: software,
        deviceMonthlyCents: deviceMonthly,
    };
}
/* ------------------------------------------------------------------------- *
 * Applying the two new dimensions
 * ------------------------------------------------------------------------- */
/**
 * Does this deal's device term reach the location at `locationIndex`?
 *
 * A single function so no caller re-implements the comparison — an off-by-one
 * here silently gives a discount away at one more location than was sold, or
 * withholds it at one that was promised it.
 *
 * `locationIndex` is 0-based and is the DRAFT ORDER, the same ordering
 * `freeLocationQuota` uses to decide which location pays no plan fee.
 */
function termAppliesAtLocation(term, locationIndex) {
    if (!term)
        return false;
    if (typeof term.coveredLocations !== 'number')
        return true; // every location
    return locationIndex < term.coveredLocations;
}
/**
 * The per-cycle amount during an intro period, given the amount it would
 * otherwise be.
 *
 * `percentOff` is applied to the amount AFTER any permanent discount, because
 * the two are different offers stacked deliberately: a device already reduced
 * by $10 and then promoted at 50% for three months is half of the reduced
 * price, which is what an operator writing both means. Rounded once, to the
 * cent, by the same rule `applyDiscount` uses — never rounded twice.
 */
function introAmountCents(intro, standardCents) {
    if (!intro)
        return undefined;
    if (typeof intro.priceCents === 'number')
        return Math.max(0, Math.round(intro.priceCents));
    if (typeof intro.percentOff === 'number') {
        return Math.max(0, Math.round(standardCents * (1 - intro.percentOff / 100)));
    }
    return undefined;
}
//# sourceMappingURL=dealDevice.js.map